import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as dashboardActions from "src/redux/slices/dashboardSlice";
import BarChart from "src/components/ui/chart/BarChart";
import Skeleton from "src/components/ui/Skeleton";
import { currencyFormatter } from "src/utils/common";
import { formatDateTime } from "src/utils/date-time";

interface SalesOverviewCardProps {
  days: number;
  periodLabel: string;
  enabled?: boolean;
  warehouseId?: string;
}

const SalesOverviewCard: React.FC<SalesOverviewCardProps> = ({
  days,
  periodLabel,
  enabled = true,
  warehouseId,
}) => {
  const dispatch = useAppDispatch();
  const { tenantSalesOverview } = useAppSelector((state) => state.dashboard);

  useEffect(() => {
    if (!enabled) return;
    dispatch(
      dashboardActions.getTenantSalesOverviewRequest({
        days,
        warehouseId: warehouseId || undefined,
      })
    );
  }, [dispatch, days, enabled, warehouseId]);

  if (!enabled) return null;

  const data = tenantSalesOverview.data?.data || [];
  const isEmpty = data.length === 0 || data.every((d) => !(d.totalSales || 0));

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Sales Overview</h2>
        <span className="text-xs text-gray-400 dark:text-gray-500">{periodLabel}</span>
      </div>
      {tenantSalesOverview.loading ? (
        <Skeleton className="mt-4 h-44 w-full" />
      ) : (
        <BarChart
          data={data}
          dataKey="totalSales"
          xKey="date"
          hideYAxis
          rotateXAxisLabels
          xTickFormatter={(value) => formatDateTime(value, "custom", "DD MMM") || value}
          barColor="#A5B4FC"
          barRadius={[8, 8, 8, 8]}
          tooltipFormatter={(value?: number) =>
            currencyFormatter(value || 0, "IN", 2)
          }
          isEmpty={isEmpty}
          emptyMessage={`No sales in the last ${days} days`}
        />
      )}
    </div>
  );
};

export default SalesOverviewCard;
